<script setup lang="ts">
/**
 * Bouton « Télécharger l'attestation » d'un contrat (PDF).
 *
 * Composant partagé par la page « Mes contrats » et la page « Souscrire un
 * produit » : le téléchargement (appel back, déclenchement du download, état de
 * chargement, gestion d'erreur, copie i18n) vit à un seul endroit.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { downloadCertificate } from '@/services/account'
import { canDownloadCertificate } from '@/config/contractCertificate'
import type { MemberContract } from '@/data/seedMember'

const props = withDefaults(
  defineProps<{
    contract: MemberContract
    /** Style du bouton : 'solid' (page contrats) ou 'pill' (cartes produit). */
    variant?: 'solid' | 'pill'
  }>(),
  { variant: 'solid' },
)

const { t } = useI18n()

/**
 * The availability rule is enforced here rather than at each call site so that a
 * new surface displaying this button cannot reintroduce the leak SI-1458 fixed.
 * Parents may still guard on their side when an empty slot would disturb their
 * layout, but they are not required to.
 */
const available = computed(() => canDownloadCertificate(props.contract))

const downloading = ref(false)
const actionError = ref<string | null>(null)

/** Récupère l'attestation PDF et déclenche son téléchargement dans le navigateur. */
async function download() {
  if (downloading.value || typeof window === 'undefined') return
  actionError.value = null
  downloading.value = true
  try {
    const blob = await downloadCertificate(props.contract.reference)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `attestation-${props.contract.reference}.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  } catch {
    actionError.value = t('auth.account.contracts.actionError')
  } finally {
    downloading.value = false
  }
}
</script>

<template>
  <!-- A terminated contract renders nothing at all — not a disabled button —
       so no dead affordance is left on screen (SI-1458). -->
  <template v-if="available">
    <button
      type="button"
      :class="variant === 'pill' ? 'cert-btn cert-btn--pill' : 'cert-btn cert-btn--solid'"
      :disabled="downloading"
      @click="download"
    >
      {{ downloading ? t('auth.account.contracts.downloading') : t('auth.account.contracts.download') }}
    </button>
    <p v-if="actionError" class="cert-btn__error" role="alert">{{ actionError }}</p>
  </template>
</template>

<style scoped>
/* Variante 'solid' : bouton compact de la page « Mes contrats » (btn-dark--sm). */
.cert-btn--solid {
  padding: 0.5rem 1rem;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--color-chocolate);
  color: var(--color-white);
  font-size: var(--text-xs);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-out);
}
.cert-btn--solid:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }

/* Variante 'pill' : action neutre dans les cartes de « Souscrire un produit ». */
.cert-btn--pill {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.7rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  background: var(--color-white);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast), color var(--dur-fast);
}
.cert-btn--pill:hover { border-color: var(--primary); color: var(--primary); }

.cert-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.cert-btn__error {
  margin-top: 0.5rem;
  font-size: var(--text-sm);
  color: var(--color-cherry);
}
</style>
